import classNames from 'classnames'
import React, { InputHTMLAttributes, ReactNode, RefObject } from 'react'
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormStateReturn,
} from 'react-hook-form'

export type RenderProps = {
  field: ControllerRenderProps<any, any>
  fieldState: ControllerFieldState
  formState: UseFormStateReturn<any>
}

export type RenderPropsTyped<
  TFields extends FieldValues,
  Key extends Path<TFields>
> = {
  field: ControllerRenderProps<TFields, Key>
  fieldState: ControllerFieldState
  formState: UseFormStateReturn<TFields>
}

//

type FieldWrapperProps = {
  label?: ReactNode
  horizontal?: boolean
  wrapperRef?: RefObject<HTMLDivElement>
  required?: boolean
  name?: string
  wrapperClassName?: string
  children: ReactNode
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  name,
  label,
  children,
  horizontal,
  wrapperRef,
  required,
  wrapperClassName,
}) => (
  <div
    className={classNames(`field ${wrapperClassName ? wrapperClassName : ''}`, {
      'is-horizontal': horizontal,
    })}
    ref={wrapperRef}
  >
    {horizontal ? (
      <div className="field-label is-normal">
        {label && (
          <label className="label" htmlFor={name}>
            {label}
            {required && ' *'}
          </label>
        )}
      </div>
    ) : (
      label && (
        <label className="label" htmlFor={name}>
          {label}
          {required && ' *'}
        </label>
      )
    )}
    <div className="field-body">{children}</div>
  </div>
)

//

type FieldProps = {
  input: ReactNode
  renderProps: RenderProps
  additional?: ReactNode
  help?: string
  label?: string
  horizontal: boolean
  prefix?: ReactNode
  suffix?: ReactNode
  wrapperRef?: RefObject<HTMLDivElement>
  required?: boolean
  wrapperClassName?: string
  withoutWrapper?: boolean
}

const Field: React.FC<FieldProps> = ({
  input,
  renderProps: { field, fieldState, formState },
  additional,
  help,
  label,
  horizontal,
  prefix,
  suffix,
  wrapperRef,
  required,
  wrapperClassName,
  withoutWrapper,
}) => {
  const error = fieldState.error && (
    <p className="help is-danger">{fieldState.error.message}</p>
  )

  const bottom = (
    <>
      {error}
      {additional}
      <Help content={help} />
    </>
  )

  const widget = (
    <>
      {!prefix && !suffix ? (
        <div className="field">
          <div className="control">{input}</div>

          {bottom}
        </div>
      ) : (
        <div className="field is-expanded">
          <div className="field has-addons">
            {prefix && (
              <p className="control">
                <a className="button is-static">{prefix}</a>
              </p>
            )}
            <p className="control is-expanded">{input}</p>
            {suffix && (
              <p className="control">
                <a className="button is-static">{suffix}</a>
              </p>
            )}
          </div>

          {bottom}
        </div>
      )}
    </>
  )

  return withoutWrapper ? (
    widget
  ) : (
    <FieldWrapper
      name={field.name}
      label={label}
      required={required}
      horizontal={horizontal}
      wrapperRef={wrapperRef}
      wrapperClassName={wrapperClassName}
    >
      {widget}
    </FieldWrapper>
  )
}

//

type HelpProps = {
  content?: string
}

export const Help: React.FC<HelpProps> = ({ content }) => {
  if (!content) {
    return null
  }

  return <p className="help">{content}</p>

  // return (
  //   <article className={classNames('message')}>
  //     <div className={classNames('message-body content', {})}>
  //       {content.split('\n').map((line, k) => (
  //         <p key={k}>{line}</p>
  //       ))}
  //     </div>
  //   </article>
  // )
}

//

const errorClassName = (renderProps: RenderProps) =>
  renderProps.fieldState.error ? 'is-danger' : ''

//

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  renderProps: RenderProps
  label?: string
  help?: string
  additional?: ReactNode
  prefix?: ReactNode
  suffix?: ReactNode
  horizontal?: boolean
  wrapperRef?: RefObject<HTMLDivElement>
  required?: boolean
  wrapperClassName?: string
  withoutWrapper?: boolean
  small?: boolean
}

export const TextField: React.FC<TextFieldProps> = ({
  renderProps,
  label,
  help,
  additional,
  prefix,
  suffix,
  horizontal = true,
  wrapperRef,
  required,
  wrapperClassName,
  withoutWrapper,
  small = false,
  ...rest
}) => (
  <Field
    renderProps={renderProps}
    wrapperRef={wrapperRef}
    input={
      <input
        {...renderProps.field}
        onChange={e => {
          if (rest['type'] == 'number') {
            const val = parseFloat(e.target.value)
            const max = rest['max'] ? parseFloat(rest['max'] + '') : Infinity
            const min = rest['min'] ? parseFloat(rest['min'] + '') : -Infinity

            if (!isNaN(max) && val > max) {
              return
            }

            if (!isNaN(min) && val < min) {
              return
            }
          }

          renderProps.field.onChange(e)
        }}
        id={renderProps.field.name}
        type="text"
        className={classNames('input', errorClassName(renderProps), {
          'is-small': small,
        })}
        required={required}
      />
    }
    additional={additional}
    help={help}
    label={label}
    prefix={prefix}
    suffix={suffix}
    horizontal={horizontal}
    required={required}
    wrapperClassName={wrapperClassName}
    withoutWrapper={withoutWrapper}
  />
)
type TextAreaFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  renderProps: RenderProps
  label?: string
  help?: string
  additional?: ReactNode
  prefix?: ReactNode
  suffix?: ReactNode
  horizontal?: boolean
  wrapperRef?: RefObject<HTMLDivElement>
  required?: boolean
  wrapperClassName?: string
  withoutWrapper?: boolean
  small?: boolean
  rows?: number
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  renderProps,
  label,
  help,
  additional,
  prefix,
  suffix,
  horizontal = true,
  wrapperRef,
  required,
  wrapperClassName,
  withoutWrapper,
  small = false,
  rows = 4,
  ...rest
}) => (
  <Field
    renderProps={renderProps}
    wrapperRef={wrapperRef}
    input={
      <textarea
        {...renderProps.field}
        id={renderProps.field.name}
        onChange={e => {
          renderProps.field.onChange(e)
        }}
        className={classNames(
          'textarea autosize',
          errorClassName(renderProps),
          {
            'is-small': small,
          }
        )}
        required={required}
        rows={rows}
      ></textarea>
    }
    additional={additional}
    help={help}
    label={label}
    prefix={prefix}
    suffix={suffix}
    horizontal={horizontal}
    required={required}
    wrapperClassName={wrapperClassName}
    withoutWrapper={withoutWrapper}
  />
)

type FieldControllerProps = {
  children: ReactNode
  label?: string
  fieldState?: ControllerFieldState | undefined
}

export const FieldController: React.FC<FieldControllerProps> = ({
  fieldState,
  children,
  label,
}) => {
  const error = fieldState && fieldState.error && (
    <p className="help is-danger">{fieldState.error.message}</p>
  )

  return (
    <div className="field">
      {label && <label className="label">{label}</label>}
      <div>{children}</div>
      {error}
    </div>
  )
}
